import React, { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import PageVisitTracker from "./components/Tracking/PageVisitTracker";

import HomePageVitrine from "./pages/Home/Home";
import LoginPage from "./pages/LoginPage/LoginPage";
import RegisterPage from "./pages/RegisterPage/RegisterPage";
import TeacherRegisterPage from "./pages/RegisterPage/TeacherRegisterPage";

import ResetPwd from "./pages/ResetPwd/ResetPwd";
import ForgotPasswordPage from "./pages/ResetPwd/ForgotPasswordPage";

import { UserProvider } from "./pages/Home/UserContext";
import PrimaryColorProvider from "./hooks/PrimaryColorProvider.jsx";
import { NotificationToastProvider } from "./components/Notification/NotificationManager.jsx";
import { SidebarProvider } from "./contexts/SidebarContext.js";
import { SiteModeProvider } from "./contexts/SiteModeContext";

import Navbar3 from "./pages/Home/Navbar3/Navbar3";
import NavbarMairie from "./pages/Home/Navbar3/NavbarMairie";
import Footer from "./pages/Home/Footer";

import { MessageProvider } from "./hooks/MessageContext.js";
import { ContactFormProvider } from "./hooks/ContactFormContext.js";

import ErrorPage from "./pages/ErrorPage/ErrorPage";

// Documents
import ConditionsUtilisationPage from "./pages/rgpd/ConditionsUtilisationPage.jsx";
import PolitiqueConfidentialitePage from "./pages/rgpd/PolitiqueConfidentialitePage.jsx";
import FaqPage from "./pages/rgpd/FaqPage.jsx";
import MentionsLegalesPage from "./pages/rgpd/MentionsLegalesPage.jsx";

// Layout Administrateur
import AsideMenu from "./components/Admin/AsideMenu";
import MainContent from "./components/Admin/MainContent";
import TopBarAdmin from "./components/Admin/TopBarAdmin";

// MonAppliMairie
import AssociationsPage from "./pages/Associations/AssociationsPage.jsx";
import ServicesMunicipauxPage from "./pages/ServicesMunicipaux/ServicesMunicipauxPage.jsx";
import TrombinosPage from "./pages/Trombinos/TrombinosPage.jsx";
import CommercesPage from "./pages/Commerces/CommercesPage.jsx";
import CommerceDetailPage from "./pages/Commerces/CommerceDetailPage.jsx";
import PageContentBuilder from "./pages/Admin/PagesAdmin/PageContentBuilder.jsx";
import PagePublic from "./pages/Public/PagePublic";
import EventsPage from "./pages/Events/EventsPage";
import NewsPage from "./pages/NewsPage/NewsPage.jsx";
import CarteInteretsPage from "./pages/CarteInterets/CarteInteretsPage.jsx";
import RecherchePage from "./pages/Recherche/RecherchePage.jsx";
import ProjetsPage from "./pages/Projets/ProjetsPage.jsx";
import PublicationsPage from "./pages/Publications/PublicationsPage.jsx";
import DocumentsPage from "./pages/Documents/DocumentsPage";
import AppPresentationPage from "./pages/Demo/AppPresentationPage";

// Thème
import useTheme from "./theme/useTheme";

import "./App.css";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error("Erreur parsing userInfo :", error);
    return null;
  }
}

function RequireAuth({ children, roles }) {
  const storedUserInfo = getStoredUserInfo();

  if (!storedUserInfo) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(Number(storedUserInfo.role_id))) {
    return <Navigate to="/404" replace />;
  }

  return children;
}

function AdminLayout() {
  return (
    <RequireAuth>
      <TopBarAdmin />

      <div className="flex min-h-screen">
        <AsideMenu />
        <MainContent />
      </div>
    </RequireAuth>
  );
}

function AppContent() {
  useTheme();

  const location = useLocation();

  const [userInfo, setUserInfo] = useState(() => getStoredUserInfo());

  const isAdminRoute = useMemo(() => {
    return location.pathname.startsWith("/appli");
  }, [location.pathname]);

  useEffect(() => {
    setUserInfo(getStoredUserInfo());
  }, [location.pathname]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "userInfo" || event.key === "token") {
        setUserInfo(getStoredUserInfo());
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const navbarUser = useMemo(() => {
    return {
      nom: userInfo?.nom ?? "Chargement...",
      prenom: userInfo?.prenom ?? "",
      role_id: userInfo?.role_id ?? 0,
      photoURL: userInfo?.photoURL ?? "",
      userId: userInfo?.userId ?? userInfo?.id ?? 0,
    };
  }, [userInfo]);

  return (
    <div className="flex min-h-screen flex-col bg-bg text-fg transition-colors">
      <PageVisitTracker />

      {!isAdminRoute && <NavbarMairie user={navbarUser} />}

      <Routes>
        {/* Accueil */}
        <Route path="/" element={<HomePageVitrine />} />

        {/* Routes admin protégées */}
        <Route path="/appli/*" element={<AdminLayout />} />

        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/register/:token" element={<TeacherRegisterPage />} />
        <Route path="/reset-password/:token" element={<ResetPwd />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        <Route path="/monapplimairie" element={<AppPresentationPage />} />

        {/* Pages légales */}
        <Route
          path="/conditions-utilisation"
          element={<ConditionsUtilisationPage />}
        />
        <Route
          path="/politique-confidentialite"
          element={<PolitiqueConfidentialitePage />}
        />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/mentions-legales" element={<MentionsLegalesPage />} />

        {/* Pages publiques MonAppliMairie */}
        <Route path="/associations" element={<AssociationsPage />} />
        <Route path="/actualites" element={<NewsPage />} />
        <Route
          path="/services-municipaux"
          element={<ServicesMunicipauxPage />}
        />
        <Route path="/trombinoscope" element={<TrombinosPage />} />
        <Route path="/commerces" element={<CommercesPage />} />
        <Route path="/commerces/:slug" element={<CommerceDetailPage />} />
        <Route path="/agenda" element={<EventsPage />} />
        <Route path="/carte-interets" element={<CarteInteretsPage />} />
        <Route path="/grands-projets" element={<ProjetsPage />} />
        <Route path="/publications" element={<PublicationsPage />} />
        <Route path="/page/:slug" element={<PagePublic />} />
        <Route path="/recherche" element={<RecherchePage />} />
        <Route path="/documents" element={<DocumentsPage />} />

        {/* Builder de page CMS */}
        <Route
          path="/admin/pages/:id/builder"
          element={
            <RequireAuth>
              <PageContentBuilder />
            </RequireAuth>
          }
        />

        {/* Erreurs */}
        <Route path="/404" element={<ErrorPage />} />
        <Route path="*" element={<ErrorPage />} />
      </Routes>

      {!isAdminRoute && <Footer />}
    </div>
  );
}

const App = () => {
  return (
    <Router>
      <UserProvider>
        <MessageProvider>
          <ContactFormProvider>
            <PrimaryColorProvider>
              <NotificationToastProvider>
                <SidebarProvider>
                  <SiteModeProvider>
                    <AppContent />
                  </SiteModeProvider>
                </SidebarProvider>
              </NotificationToastProvider>
            </PrimaryColorProvider>
          </ContactFormProvider>
        </MessageProvider>
      </UserProvider>
    </Router>
  );
};

export default App;